import React from 'react';

import { parseISOToLocale } from '@vlabs/shared/utils/date';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import classNames from 'classnames';

import { useGetDepartment } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';

import st from './DepartmentGeneralInfo.module.sass';
import { useDepartmentGeneralInfoTranslation } from './i18n';
import { buildTestid, valueOrHyphen } from '../../../shared/lib/utils';

interface DepartmentGeneralInfoProps extends WithDataTestid, WithClassNameProp {
  departmentId: DepartmentId;
}

const displayName = 'DepartmentGeneralInfo';
const testid = buildTestid(displayName);

function DepartmentGeneralInfo({
  departmentId,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: DepartmentGeneralInfoProps): React.JSX.Element {
  const { t } = useDepartmentGeneralInfoTranslation();
  const getDepartment = useGetDepartment(departmentId);

  const departmentName = valueOrHyphen(getDepartment.data?.name);
  const description = valueOrHyphen(getDepartment.data?.description);
  const createdAt = valueOrHyphen(parseISOToLocale(getDepartment.data?.create_time));
  const updatedAt = valueOrHyphen(parseISOToLocale(getDepartment.data?.update_time));

  return (
    <div
      className={classNames(st.Data, className)}
      {...testid(dataTestId)}
    >
      <div className={st.Row}>
        <span className={st.Label}>{t('fields.name')}</span>
        <span className={st.Value}>{departmentName}</span>
      </div>

      <div className={st.Divider} />

      <div className={st.Row}>
        <span className={st.Label}>{t('fields.description')}</span>
        <span className={classNames(st.Value, st.Value_multiline)}>{description}</span>
      </div>

      <div className={st.Divider} />

      <div className={st.Row}>
        <span className={st.Label}>{t('fields.created')}</span>
        <span className={st.Value}>{createdAt}</span>
      </div>

      <div className={st.Divider} />

      <div className={st.Row}>
        <span className={st.Label}>{t('fields.updated')}</span>
        <span className={st.Value}>{updatedAt}</span>
      </div>

      <div className={st.Divider} />
    </div>
  );
}

DepartmentGeneralInfo.displayName = displayName;

export { DepartmentGeneralInfo };
